import { useMemo, useState, useEffect } from 'react'
import { useSnapshot } from 'valtio'
import { lastConnectOptions } from '../appStatus'
import type { ReplayEvent } from '../packetsReplay/extractReplayEvents'
import { filterPackets } from './packetsFilter'
import { packetsReplayState } from './state/packetsReplayState'
import PacketList from './components/replay/PacketList'
import FilterInput from './components/replay/FilterInput'
import Screen from './Screen'
import styles from './components/replay/ReplayPanel.module.css'

interface Props {
  replayName: string
  packets: readonly PacketData[]
  isPlaying: boolean
  progress: { current: number; total: number }
  speed: number
  defaultFilter?: string
  customButtons: Readonly<Record<string, { state: boolean; label: string; tooltip?: string }>>
  onPlayPause?: (isPlaying: boolean) => void
  onRestart?: () => void
  onSpeedChange?: (speed: number) => void
  onFilterChange: (filter: string) => void
  onCustomButtonToggle: (buttonId: string) => void
  clientPacketsAutocomplete: string[]
  serverPacketsAutocomplete: string[]
  style?: React.CSSProperties
  replayEvents?: ReplayEvent[]
  totalTimeMs?: number
  onSeekToTime?: (timeMs: number) => void
  hidePlaybackBar?: boolean
}

export default function ReplayPanel ({
  replayName,
  packets,
  isPlaying,
  progress,
  speed,
  defaultFilter = '',
  onPlayPause,
  onRestart,
  onSpeedChange,
  onFilterChange,
  clientPacketsAutocomplete,
  serverPacketsAutocomplete,
  style,
  hidePlaybackBar = false,
}: Props) {
  const [filter, setFilter] = useState(defaultFilter)
  const { isMinimized, isRecording, showPacketList, seekTo, replayMinecraftVersion } = useSnapshot(packetsReplayState)
  const isReplaySession = !!lastConnectOptions.value?.worldStateFileContents
  const recordingUiMode = isRecording && !isReplaySession
  const { filtered: filteredPackets, hiddenCount } = useMemo(() => {
    if (!showPacketList) return { filtered: [] as PacketData[], hiddenCount: 0 }
    return filterPackets(packets.slice(-500), filter)
  }, [showPacketList, packets, filter])

  useEffect(() => {
    onFilterChange(filter)
  }, [filter, onFilterChange])

  const closeInspector = () => {
    if (hidePlaybackBar) {
      packetsReplayState.showPacketList = false
    } else {
      packetsReplayState.isMinimized = true
    }
  }

  // Esc closes the inspector, like the keybindings modal. Ignore Esc while the
  // filter input is focused (it dismisses the autocomplete there instead).
  useEffect(() => {
    if (isMinimized || hidePlaybackBar && !showPacketList) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      e.preventDefault()
      e.stopPropagation()
      closeInspector()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [isMinimized, hidePlaybackBar, showPacketList])

  const handlePlayPauseClick = () => {
    if (isMinimized) {
      packetsReplayState.isMinimized = false
    } else {
      onPlayPause?.(!isPlaying)
    }
  }

  const stopAndUnlock = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    document.exitPointerLock?.()
  }

  if (hidePlaybackBar && isMinimized) return null

  if (isMinimized) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 18,
          right: 0,
          zIndex: 9,
          padding: '16px',
          ...style,
        }}
        onMouseDown={stopAndUnlock}
        onPointerDown={stopAndUnlock}
        data-replay-ui
      >
        <button onClick={handlePlayPauseClick} className={styles.miniBtn}>
          {recordingUiMode ? 'Rec' : '▶ Play'}
        </button>
      </div>
    )
  }

  const subtitle = recordingUiMode
    ? 'Recording packets…'
    : 'Integrated server emulation · Testing client…'

  return (
    <Screen title="" backdrop className={styles.screen}>
      <div
        className={styles.panel}
        onMouseDown={stopAndUnlock}
        onPointerDown={stopAndUnlock}
        data-replay-ui
      >
        <div className={styles.panelSurface}>
          <div className={styles.head}>
            <div className={styles.headRow}>
              <span className={styles.dia} />
              <span className={styles.title}>Пакеты</span>
              <span className={`${styles.badge} ${recordingUiMode ? styles.badgeRec : ''}`}>
                {recordingUiMode ? 'Запись' : `MC ${replayMinecraftVersion ?? '?'}`}
              </span>
              <button className={styles.closeBtn} onClick={closeInspector} aria-label="Закрыть">EXIT</button>
            </div>
            <div className={styles.headMeta}>
              <span className={styles.subId}>{replayName || 'Unnamed Replay'}</span>
              <span className={styles.subDesc}>{subtitle}</span>
            </div>
          </div>

          <div className={styles.hr} />

          {showPacketList && (
            <div className={styles.body}>
              <FilterInput
                value={filter}
                onChange={setFilter}
                hiddenCount={hiddenCount}
                shownCount={filteredPackets.length}
                onClearFilter={() => setFilter('')}
                clientPacketsAutocomplete={clientPacketsAutocomplete}
                serverPacketsAutocomplete={serverPacketsAutocomplete}
              />
              <div className={styles.hr} />
              <PacketList
                packets={filteredPackets}
                maxHeight={360}
              />
            </div>
          )}

          {!hidePlaybackBar && (
            <div className={styles.playbackBar}>
              <div className={styles.pbControls}>
                <button
                  onClick={handlePlayPauseClick}
                  className={`${styles.pbBtn} ${isPlaying ? styles.pause : styles.active}`}
                >
                  {recordingUiMode ? 'Rec' : (isPlaying ? '⏸ Pause' : '▶ Play')}
                </button>
                <button className={`${styles.pbBtn} ${styles.seekBtn}`} onClick={() => onSpeedChange?.(1)} title="Speed 1x">›</button>
                <button className={`${styles.pbBtn} ${styles.seekBtn}`} onClick={() => onSpeedChange?.(2)} title="Speed 2x">»»</button>
                <button className={`${styles.pbBtn} ${styles.restart}`} onClick={onRestart}>↺ Restart</button>

                <div className={styles.pbMeta}>
                  <div className={styles.pbSpeed}>{recordingUiMode ? 'Rec' : (isPlaying ? 'Play' : 'Pause')} ×{speed}</div>
                  <div className={styles.pbCounter}>
                    <strong>{typeof seekTo === 'number' ? seekTo : progress.current}</strong> / {progress.total}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Screen>
  )
}

export interface PacketData {
  name: string
  data: any
  isFromClient: boolean
  isUpcoming: boolean
  actualVersion?: any
  position: number
  timestamp: number
  isCustomChannel?: boolean
}
